import { ColumnDef } from '@tanstack/react-table';
import { AlertCircle, CheckCircle, Eye, MoreHorizontal, Pencil, Play, Trash2, UserPlus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Task, TaskStatus } from '../types/task';
import CreateTaskModal from './CreateTaskModal';
import TaskStatusBadge from './TaskStatusBadge';
import { ConfirmDialog, MessageDialog } from './ui/alert-dialog-helper';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { createDateColumn } from './ui/column-helpers';
import { DataTable } from './ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

interface TaskListProps {
  jobId: string;
  editable?: boolean;
  onTaskUpdate?: () => void;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

const TaskList: React.FC<TaskListProps> = ({ jobId, editable = true, onTaskUpdate }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [assignUserId, setAssignUserId] = useState('');
  const [users, setUsers] = useState<User[]>([]);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadTasks();
    if (editable) {
      loadUsers();
    }
  }, [jobId]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setTasks(data.data);
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users?is_active=true&limit=10', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.users) {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleStartTask = async (task: Task) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${task.id}/start`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        loadTasks();
        onTaskUpdate?.();
      }
    } catch (err) {
      console.error('Error starting task:', err);
    }
  };

  const handleCompleteTask = async () => {
    if (!selectedTask) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${selectedTask.id}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ completion_notes: completionNotes }),
      });
      const data = await response.json();
      if (data.success) {
        loadTasks();
        onTaskUpdate?.();
        setCompleteModalOpen(false);
        setCompletionNotes('');
      }
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const handleBlockTask = async () => {
    if (!selectedTask || !blockReason) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${selectedTask.id}/block`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: blockReason }),
      });
      const data = await response.json();
      if (data.success) {
        loadTasks();
        onTaskUpdate?.();
        setBlockModalOpen(false);
        setBlockReason('');
      }
    } catch (err) {
      console.error('Error blocking task:', err);
    }
  };

  const handleAssignTask = async () => {
    if (!selectedTask || !assignUserId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${selectedTask.id}/assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: assignUserId }),
      });
      const data = await response.json();
      if (data.success) {
        loadTasks();
        onTaskUpdate?.();
        setAssignModalOpen(false);
        setAssignUserId('');
      }
    } catch (err) {
      console.error('Error assigning task:', err);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/${taskToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        loadTasks();
        onTaskUpdate?.();
      } else {
        setErrorMessage(data.message || 'Failed to delete task');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      setErrorMessage('An error occurred while deleting the task');
      setErrorDialogOpen(true);
    } finally {
      setTaskToDelete(null);
    }
  };

  const formatTaskType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getPriorityBadge = (priority: string) => {
    const priorityMap: Record<string, 'default' | 'warning' | 'destructive' | 'secondary'> = {
      urgent: 'destructive',
      high: 'warning',
      normal: 'default',
      low: 'secondary',
    };
    return priorityMap[priority] || 'default';
  };

  const columns: ColumnDef<Task>[] = [
    {
      accessorKey: 'task_number',
      header: 'Task Number',
      cell: ({ row }) => (
        <button
          onClick={() => navigate(`/tasks/${row.original.id}`, { state: { returnTo: 'job', jobId, tab: 'tasks' } })}
          className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
        >
          {row.original.task_number}
        </button>
      ),
    },
    {
      accessorKey: 'task_type',
      header: 'Type',
      cell: ({ row }) => formatTaskType(row.original.task_type),
    },
    {
      accessorKey: 'assigned_to_user',
      header: 'Assigned To',
      cell: ({ row }) => {
        const assignedUser = row.original.assigned_to_user;
        return assignedUser
          ? `${assignedUser.first_name} ${assignedUser.last_name}`
          : <span className="text-muted-foreground italic">Unassigned</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <TaskStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => (
        <Badge variant={getPriorityBadge(row.original.priority)}>
          {row.original.priority.toUpperCase()}
        </Badge>
      ),
    },
    createDateColumn('planned_start_at', 'Planned Start'),
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const task = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}`, { state: { returnTo: 'job', jobId, tab: 'tasks' } })}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              {editable && (
                <>
                  <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}/edit`, { state: { returnTo: 'job', jobId, tab: 'tasks' } })}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  {task.status === TaskStatus.PENDING && (
                    <DropdownMenuItem
                      onClick={() => {
                        setSelectedTask(task);
                        setAssignUserId(task.assigned_to_user_id || '');
                        setAssignModalOpen(true);
                      }}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign
                    </DropdownMenuItem>
                  )}
                  {task.status === TaskStatus.ASSIGNED && (
                    <DropdownMenuItem onClick={() => handleStartTask(task)}>
                      <Play className="h-4 w-4 mr-2" />
                      Start
                    </DropdownMenuItem>
                  )}
                  {task.status === TaskStatus.IN_PROGRESS && (
                    <>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedTask(task);
                          setCompleteModalOpen(true);
                        }}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Complete
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedTask(task);
                          setBlockModalOpen(true);
                        }}
                      >
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Block
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => {
                      setTaskToDelete(task);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        data={tasks}
        loading={loading}
        searchKey="task_number"
        searchPlaceholder="Search tasks..."
        toolbarContentRight={
          editable ? (
            <Button onClick={() => setCreateModalOpen(true)}>Add Task</Button>
          ) : undefined
        }
        emptyMessage="No tasks yet. Click 'Add Task' to create one."
      />

      <CreateTaskModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        jobId={jobId}
        taskOrder={tasks.length + 1}
        onSuccess={() => {
          loadTasks();
          onTaskUpdate?.();
        }}
      />

      {/* Complete Task Modal */}
      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Completion Notes</Label>
              <Textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Add any notes about task completion..."
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCompleteModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCompleteTask}>Complete Task</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block Task Modal */}
      <Dialog open={blockModalOpen} onOpenChange={setBlockModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for Blocking *</Label>
              <Textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Why is this task blocked?"
                rows={4}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBlockModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleBlockTask} disabled={!blockReason}>
                Block Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Task Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Assign To *</Label>
              <Select value={assignUserId} onValueChange={setAssignUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAssignModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignTask} disabled={!assignUserId}>
                Assign Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Task"
        description={`Are you sure you want to delete task ${taskToDelete?.task_number}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteTask}
        variant="destructive"
      />

      {/* Error Message Dialog */}
      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

export default TaskList;
